import { RolePlayer, SpecialRole } from "./RoleDistributorReducer";
import { AbilityUsage } from "./abilities";

// 역할 룰렛 게임용 소켓 메시지 타입 정의
// 예시: 라벨 세팅, 배정, 시작/종료 등

// export interface RoleLabelSettingMessage { ... }
// export interface RoleAssignmentMessage { ... }

export enum RoleDistributorProtocol {
  // 플레이어 목록 관련
  PHASE_READY = "ROLE_DISTRIBUTOR_PHASE_READY",
  PLAYER_JOINED = "ROLE_DISTRIBUTOR_PLAYER_JOINED",
  PLAYER_LEFT = "ROLE_DISTRIBUTOR_PLAYER_LEFT",

  // 역할 룰렛 관련
  DISTRIBUTION_START = "ROLE_DISTRIBUTOR_DISTRIBUTION_START",
  DISTRIBUTION_END = "ROLE_DISTRIBUTOR_DISTRIBUTION_END",
  ROLES_ASSIGNED = "ROLE_DISTRIBUTOR_ROLES_ASSIGNED",

  // 설정 관련
  SETTINGS_COMPLETED = "ROLE_DISTRIBUTOR_SETTINGS_COMPLETED",
  SETTINGS_RESET = "ROLE_DISTRIBUTOR_SETTINGS_RESET",

  // 특수능력 관련
  ABILITY_PHASE_START = "ROLE_DISTRIBUTOR_ABILITY_PHASE_START",
  ABILITY_PHASE_END = "ROLE_DISTRIBUTOR_ABILITY_PHASE_END",
  ABILITY_USED = "ROLE_DISTRIBUTOR_ABILITY_USED",
  ABILITY_CANCELED = "ROLE_DISTRIBUTOR_ABILITY_CANCELED", // 액션 취소
  ABILITY_RESULT = "ROLE_DISTRIBUTOR_ABILITY_RESULT",
  ABILITY_RESULTS_BATCH = "ROLE_DISTRIBUTOR_ABILITY_RESULTS_BATCH", // 배치 결과 처리
  VOTING_RESULT = "ROLE_DISTRIBUTOR_VOTING_RESULT", // 투표 결과 집계

  // 재연결
  SYNC_GAME_STATE = "roledistributor:sync_game_state",

  // 에러
  ERROR = "ROLE_DISTRIBUTOR_ERROR",
}

export interface RoleDistributorProtocolData {
  [RoleDistributorProtocol.PHASE_READY]: {
    labels: SpecialRole[];
    defaultLabel: { name: string; explain: string };
    players: RolePlayer[];
    hostReveal: boolean;
    started: boolean;
    hostId: number;
    settingsCompleted: boolean;
    abilityPhase: { isActive: boolean; startedBy: number; startTime?: number; duration?: number; endTime?: number };
    abilityUsages: AbilityUsage[];
  };
  [RoleDistributorProtocol.PLAYER_JOINED]: {
    player: RolePlayer;
  };
  [RoleDistributorProtocol.PLAYER_LEFT]: {
    playerId: number;
  };
  [RoleDistributorProtocol.DISTRIBUTION_START]: {
    players: RolePlayer[]; // 역할이 배정된 플레이어들
    defaultRole: string;
    specialRoles: SpecialRole[];
  };
  [RoleDistributorProtocol.DISTRIBUTION_END]: {
    players: RolePlayer[]; // 역할이 초기화된 플레이어들
  };
  [RoleDistributorProtocol.ROLES_ASSIGNED]: {
    players: RolePlayer[]; // 역할이 배정된 플레이어들
    defaultLabel: { name: string; explain: string };
    labels: SpecialRole[];
  };
  [RoleDistributorProtocol.SETTINGS_COMPLETED]: {
    defaultLabel: { name: string; explain: string };
    labels: SpecialRole[];
    hostReveal: boolean;
  };
  [RoleDistributorProtocol.SETTINGS_RESET]: {
    // 빈 객체 (설정 초기화만 알림)
  };
  [RoleDistributorProtocol.ABILITY_PHASE_START]: {
    startedBy: number;
    startTime: number;
    duration?: number; // 타이머 지속 시간 (초)
    endTime?: number; // 종료 예정 시간 (timestamp)
  };
  [RoleDistributorProtocol.ABILITY_PHASE_END]: {
    endedBy: number;
    totalUsages: number;
    results: Array<{
      message: string;
      type: "public" | "private";
      targetPlayerId?: number;
      abilityId?: string;
      success: boolean;
    }>;
    timestamp: number;
  };
  [RoleDistributorProtocol.ABILITY_USED]: {
    usage: AbilityUsage;
    playerId: number;
    abilityId: string;
    targetId?: number;
    choice?: boolean;
  };
  [RoleDistributorProtocol.ABILITY_CANCELED]: {
    playerId: number;
    abilityId: string;
    timestamp: number;
  };
  [RoleDistributorProtocol.ABILITY_RESULT]: {
    usage: AbilityUsage;
    result: string;
    success: boolean;
  };
  [RoleDistributorProtocol.ABILITY_RESULTS_BATCH]: {
    processedResults: AbilityUsage[];
    publicResults: AbilityUsage[]; // 모든 플레이어에게 공개될 결과
    privateResults: { [playerId: number]: AbilityUsage[] }; // 개별 플레이어에게만 전달될 결과
    timestamp: number;
  };
  [RoleDistributorProtocol.VOTING_RESULT]: {
    abilityId: string;
    resultType: 'public' | 'private';
    // 투표/지목 결과 (PRIVATE 타입일 경우)
    voteResults?: { [targetId: number]: number }; // 플레이어ID: 득표수
    // 찬반투표 결과 (PRIVATE 타입일 경우)
    choiceResults?: { agree: number; disagree: number };
    totalVotes: number;
    timestamp: number;
  };
  [RoleDistributorProtocol.SYNC_GAME_STATE]: {
    phase: 'waiting' | 'distributed';
    isDistributed: boolean;
    isSettingsCompleted: boolean;
    defaultLabel: {
      name: string;
      explain: string;
      abilities: string[];
    };
    labels: SpecialRole[];
    players: RolePlayer[];
    hostReveal: boolean;
    hostId: number;
    abilityPhase: {
      isActive: boolean;
      startedBy: number;
      startTime: number | null;
      duration?: number | null;
      endTime?: number | null;
    };
  };
  [RoleDistributorProtocol.ERROR]: {
    message: string;
    code: string;
  };
}
